import type { JsonSchema, WebMCPTool } from '@cogo/webmcp-runtime'

export type StudioExactImpactReadbackInput =
  | { mode: 'capture'; paths: string[] }
  | {
      mode?: 'assert'
      checks: Array<
        | { path: string; expectation: 'equals'; expected: unknown }
        | { path: string; expectation: 'absent' }
        | { path: string; expected: unknown }
      >
    }

export interface StudioExactImpactAuthorityObservation {
  presence: boolean
  value?: unknown
}

export interface StudioExactImpactReadbackDependencies {
  readCurrentAuthority(path: string): StudioExactImpactAuthorityObservation
  sanitizeValue(value: unknown): unknown
}

type StudioExactImpactAssertion =
  | { path: string; expectation: 'equals'; expected: unknown }
  | { path: string; expectation: 'absent' }

const CANONICAL_ENCODED_ID_SEGMENT =
  `(?:[A-Za-z0-9_.!~*'()-]|(?!%25[0-9A-Fa-f]{2})%(?!(?:2F|5C))[0-9A-F]{2})+`
const PAGE_TITLE_PATH_PATTERN =
  `^/manifest/pages/${CANONICAL_ENCODED_ID_SEGMENT}/title$`
const DOCUMENT_NODE_TEXT_PATH_PATTERN =
  `^/cogoDocumentWorkspace/nodes/${CANONICAL_ENCODED_ID_SEGMENT}/text$`
const PAGE_TITLE_PATH = /^\/manifest\/pages\/([^/]+)\/title$/
const DOCUMENT_NODE_TEXT_PATH =
  /^\/cogoDocumentWorkspace\/nodes\/([^/]+)\/text$/
const CANONICAL_ENCODED_ID = new RegExp(`^${CANONICAL_ENCODED_ID_SEGMENT}$`)
const pathSchema: JsonSchema = {
  type: 'string',
  minLength: 1,
  anyOf: [
    { pattern: PAGE_TITLE_PATH_PATTERN },
    { enum: ['/tokens/colors/primary'] },
    { pattern: DOCUMENT_NODE_TEXT_PATH_PATTERN },
  ],
}

export type StudioExactImpactTarget =
  | { kind: 'page-title'; pageId: string }
  | { kind: 'primary-color' }
  | { kind: 'document-node-text'; documentNodeId: string }

const equalsCheckSchema: JsonSchema = {
  type: 'object',
  required: ['path', 'expectation', 'expected'],
  properties: {
    path: pathSchema,
    expectation: { type: 'string', enum: ['equals'] },
    expected: {},
  },
  additionalProperties: false,
}

const absentCheckSchema: JsonSchema = {
  type: 'object',
  required: ['path', 'expectation'],
  properties: {
    path: pathSchema,
    expectation: { type: 'string', enum: ['absent'] },
  },
  additionalProperties: false,
}

const legacyEqualsCheckSchema: JsonSchema = {
  type: 'object',
  required: ['path', 'expected'],
  properties: {
    path: pathSchema,
    expectation: { type: 'string', enum: ['equals'] },
    expected: {},
  },
  additionalProperties: false,
}

export const studioExactImpactReadbackInputSchema: JsonSchema = {
  type: 'object',
  anyOf: [
    {
      type: 'object',
      required: ['mode', 'paths'],
      properties: {
        mode: { type: 'string', enum: ['capture'] },
        paths: {
          type: 'array',
          minItems: 1,
          items: pathSchema,
        },
      },
      additionalProperties: false,
    },
    {
      type: 'object',
      required: ['checks'],
      properties: {
        mode: { type: 'string', enum: ['assert'] },
        checks: {
          type: 'array',
          minItems: 1,
          items: {
            anyOf: [equalsCheckSchema, absentCheckSchema, legacyEqualsCheckSchema],
          },
        },
      },
      additionalProperties: false,
    },
  ],
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function decodeCanonicalTargetId(segment: string, path: string): string {
  if (!CANONICAL_ENCODED_ID.test(segment)) {
    throw new Error(
      `EXACT_IMPACT_PATH_ENCODING_INVALID: exact-impact target has invalid encoded ID: ${path}.`,
    )
  }
  let decoded: string
  try {
    decoded = decodeURIComponent(segment)
  } catch {
    throw new Error(
      `EXACT_IMPACT_PATH_ENCODING_INVALID: exact-impact target has invalid encoded ID: ${path}.`,
    )
  }
  if (
    decoded.includes('/') ||
    decoded.includes('\\') ||
    /%[0-9A-Fa-f]{2}/.test(decoded) ||
    encodeURIComponent(decoded) !== segment
  ) {
    throw new Error(
      `EXACT_IMPACT_PATH_ENCODING_INVALID: exact-impact target has invalid encoded ID: ${path}.`,
    )
  }
  return decoded
}

export function classifyStudioExactImpactTarget(
  value: unknown,
): StudioExactImpactTarget {
  if (typeof value !== 'string' || !value.startsWith('/') || value.length < 2) {
    throw new Error(
      'EXACT_IMPACT_INPUT_INVALID: each readback target requires an absolute JSON path.',
    )
  }
  if (value === '/tokens/colors/primary') return { kind: 'primary-color' }
  const pageTitle = PAGE_TITLE_PATH.exec(value)
  if (pageTitle) {
    return {
      kind: 'page-title',
      pageId: decodeCanonicalTargetId(pageTitle[1], value),
    }
  }
  const documentNodeText = DOCUMENT_NODE_TEXT_PATH.exec(value)
  if (documentNodeText) {
    return {
      kind: 'document-node-text',
      documentNodeId: decodeCanonicalTargetId(documentNodeText[1], value),
    }
  }
  throw new Error(
    `EXACT_IMPACT_UNSUPPORTED_TARGET: Studio exact-impact readback does not support ${value}.`,
  )
}

function requireSupportedExactPath(value: unknown): string {
  classifyStudioExactImpactTarget(value)
  return value as string
}

function requireOnlyKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  scope: string,
): void {
  const extras = Object.keys(value).filter((key) => !allowed.includes(key))
  if (extras.length > 0) {
    throw new Error(
      `EXACT_IMPACT_INPUT_INVALID: ${scope} contains unsupported field ${extras[0]}.`,
    )
  }
}

function normalizeAssertions(value: unknown): StudioExactImpactAssertion[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error('EXACT_IMPACT_INPUT_INVALID: at least one exact impact assertion is required.')
  }
  return value.map((candidate) => {
    const check = recordOf(candidate)
    if (!check) {
      throw new Error('EXACT_IMPACT_INPUT_INVALID: each exact impact assertion must be an object.')
    }
    const path = requireSupportedExactPath(check.path)
    if (check.expectation === 'absent') {
      requireOnlyKeys(check, ['path', 'expectation'], 'absent assertion')
      return { path, expectation: 'absent' }
    }
    if (
      (check.expectation === undefined || check.expectation === 'equals') &&
      Object.prototype.hasOwnProperty.call(check, 'expected')
    ) {
      requireOnlyKeys(
        check,
        check.expectation === 'equals'
          ? ['path', 'expectation', 'expected']
          : ['path', 'expected'],
        'equals assertion',
      )
      return { path, expectation: 'equals', expected: check.expected }
    }
    throw new Error(
      'EXACT_IMPACT_INPUT_INVALID: each assertion requires equals with expected, or absent.',
    )
  })
}

function canonicalize(value: unknown): unknown {
  if (value === undefined) return ['undefined']
  if (value === null) return ['null']
  if (typeof value === 'string') return ['string', value]
  if (typeof value === 'boolean') return ['boolean', value]
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return ['number', 'NaN']
    if (value === Number.POSITIVE_INFINITY) return ['number', 'Infinity']
    if (value === Number.NEGATIVE_INFINITY) return ['number', '-Infinity']
    if (Object.is(value, -0)) return ['number', '-0']
    return ['number', value]
  }
  if (typeof value === 'bigint') return ['bigint', value.toString()]
  if (Array.isArray(value)) return ['array', value.map(canonicalize)]
  const record = recordOf(value)
  if (!record) return [typeof value, String(value)]
  return [
    'object',
    Object.keys(record)
      .sort()
      .map((key) => [key, canonicalize(record[key])]),
  ]
}

function canonicalString(value: unknown): string {
  return JSON.stringify(canonicalize(value))
}

function stableHash(value: string): string {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function captureObservation(
  path: string,
  dependencies: StudioExactImpactReadbackDependencies,
): Record<string, unknown> {
  const current = dependencies.readCurrentAuthority(path)
  return current.presence
    ? {
        path,
        presence: true,
        value: dependencies.sanitizeValue(current.value),
      }
    : { path, presence: false }
}

export function readStudioExactImpact(
  input: Record<string, unknown>,
  dependencies: StudioExactImpactReadbackDependencies,
): {
  observations: Array<Record<string, unknown>>
  verified: true
  fingerprint: string
  capturedAt: string
} {
  let observations: Array<Record<string, unknown>>
  if (input.mode === 'capture') {
    requireOnlyKeys(input, ['mode', 'paths'], 'capture input')
    if (!Array.isArray(input.paths) || input.paths.length === 0) {
      throw new Error(
        'EXACT_IMPACT_INPUT_INVALID: capture requires at least one exact impact path.',
      )
    }
    observations = input.paths.map((path) =>
      captureObservation(requireSupportedExactPath(path), dependencies),
    )
  } else {
    if (input.mode !== undefined && input.mode !== 'assert') {
      throw new Error('EXACT_IMPACT_INPUT_INVALID: mode must be capture or assert.')
    }
    requireOnlyKeys(
      input,
      input.mode === 'assert' ? ['mode', 'checks'] : ['checks'],
      'assert input',
    )
    observations = normalizeAssertions(input.checks).map((check) => {
      const current = dependencies.readCurrentAuthority(check.path)
      if (check.expectation === 'absent') {
        if (current.presence) {
          throw new Error(
            `EXACT_IMPACT_PRESENCE_MISMATCH: ${check.path} is present; expected it to be absent.`,
          )
        }
        return {
          path: check.path,
          expectation: 'absent',
          presence: false,
          verified: true,
        }
      }
      if (!current.presence || canonicalString(current.value) !== canonicalString(check.expected)) {
        throw new Error(
          `EXACT_IMPACT_VALUE_MISMATCH: ${check.path} did not equal the expected value.`,
        )
      }
      return {
        path: check.path,
        expectation: 'equals',
        expected: dependencies.sanitizeValue(check.expected),
        presence: true,
        value: dependencies.sanitizeValue(current.value),
        verified: true,
      }
    })
  }

  return {
    observations,
    verified: true,
    fingerprint: stableHash(canonicalString(observations)),
    capturedAt: new Date().toISOString(),
  }
}

export function createStudioExactImpactReadbackTool(
  dependencies: StudioExactImpactReadbackDependencies,
): Omit<WebMCPTool, 'name'> {
  return {
    description:
      'Capture or assert an exact page title, primary color, or document-node text path against the current active Studio authority. This is local working-copy evidence, not server persistence or publication evidence.',
    metadata: {
      readOnly: true,
      executionAuthority: 'studio',
      tags: ['chat', 'webmcp', 'readback', 'exact-impact', 'insights'],
    },
    inputSchema: studioExactImpactReadbackInputSchema,
    handler: (payload) => readStudioExactImpact(payload, dependencies),
  }
}
